import { Page } from '@/components/dashboard/page';
import { Table } from '@/components/table';
import { api } from '@/api';
import { createFileRoute } from '@tanstack/react-router';
import { Heading, Small } from 'ht-sdk/react';
import type { TeamName } from '@shared/modelTypes/session-types';
import { useState } from 'react';

export const Route = createFileRoute('/dashboard/ranking/')({
	component: RouteComponent,
	loader: async () => {
		const result = await api.rankingData({});
		return result.success ? result.data : { topSessions: [], topCrews: [], topSessionsByDamage: [] };
	},
});

const teamColors: Record<TeamName, string> = {
	alpha: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
	beta: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
	delta: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
	gamma: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const teamLabels: Record<TeamName, string> = {
	alpha: 'Alpha',
	beta: 'Beta',
	delta: 'Delta',
	gamma: 'Gamma',
};

const medalStyle = ['text-yellow-500 font-bold', 'text-gray-400 font-bold', 'text-amber-600 font-bold'];

function TeamBadge({ team }: { team: TeamName }) {
	return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${teamColors[team]}`}>{teamLabels[team]}</span>;
}

function RankCell({ rank }: { rank: number }) {
	const style = rank < 3 ? medalStyle[rank] : 'text-gray-500';
	return <span className={style}>{rank + 1}</span>;
}

function formatDateTime(dateStr: string | null): string {
	if (!dateStr) return '—';
	return new Date(dateStr).toLocaleString('nl-NL', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

const teamNames = ['alpha', 'beta', 'delta', 'gamma'] as const;

type TeamExportStats = {
	score: number;
	roomsConnected: number;
	repairsSuccessful: number;
	repairsAborted: number;
	timeExtensions: number;
	bonusCodes: number;
	connectionsLost: number;
	connectionsRestored: number;
};

type SessionExportRow = {
	sessionId: string;
	locationName: string;
	date: string | null;
	totalScore: number;
	systemDamage: number;
	durationMinutes: number | null;
	totalRoomsConnected: number;
	totalRepairsSuccessful: number;
	totalRepairsAborted: number;
	totalTimeExtensions: number;
	totalBonusCodes: number;
	totalConnectionsLost: number;
	totalConnectionsRestored: number;
	teams: Record<string, TeamExportStats>;
};

function escapeCsvCell(value: string | number | null | undefined): string {
	const str = value == null ? '' : String(value);
	if (str.includes(';') || str.includes('"') || str.includes('\n')) {
		return `"${str.replace(/"/g, '""')}"`;
	}
	return str;
}

function buildCsv(rows: SessionExportRow[]): string {
	const teamHeaders = teamNames.flatMap((t) => [
		`${t}_score`,
		`${t}_kamers_verbonden`,
		`${t}_reparaties_geslaagd`,
		`${t}_reparaties_afgebroken`,
		`${t}_tijd_toegevoegd`,
		`${t}_bonuscodes`,
		`${t}_connecties_verloren`,
		`${t}_connecties_hersteld`,
	]);

	const headers = [
		'sessie_id',
		'locatie',
		'datum',
		'tijd',
		'score_totaal',
		'klanttevredenheid',
		'speelduur_minuten',
		'kamers_verbonden',
		'reparaties_geslaagd',
		'reparaties_afgebroken',
		'tijd_toegevoegd',
		'bonuscodes',
		'connecties_verloren',
		'connecties_hersteld',
		...teamHeaders,
	];

	const dataRows = rows.map((r) => {
		const dt = r.date ? new Date(r.date) : null;
		const date = dt ? dt.toLocaleDateString('nl-NL') : '';
		const time = dt ? dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '';

		const teamCells = teamNames.flatMap((t) => {
			const ts = r.teams[t];
			return [
				ts?.score ?? 0,
				ts?.roomsConnected ?? 0,
				ts?.repairsSuccessful ?? 0,
				ts?.repairsAborted ?? 0,
				ts?.timeExtensions ?? 0,
				ts?.bonusCodes ?? 0,
				ts?.connectionsLost ?? 0,
				ts?.connectionsRestored ?? 0,
			];
		});

		return [
			r.sessionId,
			r.locationName,
			date,
			time,
			r.totalScore,
			100 - r.systemDamage,
			r.durationMinutes ?? '',
			r.totalRoomsConnected,
			r.totalRepairsSuccessful,
			r.totalRepairsAborted,
			r.totalTimeExtensions,
			r.totalBonusCodes,
			r.totalConnectionsLost,
			r.totalConnectionsRestored,
			...teamCells,
		]
			.map(escapeCsvCell)
			.join(';');
	});

	return [headers.join(';'), ...dataRows].join('\n');
}

function CsvExportButton() {
	const [loading, setLoading] = useState(false);

	async function handleExport() {
		setLoading(true);
		try {
			const response = (await api.sessionExport({})) as unknown as { success: boolean; data: SessionExportRow[]; message?: string };
			if (!response.success) throw new Error(response.message ?? 'Export mislukt');
			const csv = buildCsv(response.data);
			const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `sessies-export-${new Date().toISOString().slice(0, 10)}.csv`;
			a.click();
			URL.revokeObjectURL(url);
		} finally {
			setLoading(false);
		}
	}

	return (
		<button
			onClick={handleExport}
			disabled={loading}
			className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
		>
			{loading ? 'Exporteren...' : 'CSV export'}
		</button>
	);
}

function RouteComponent() {
	const { topSessions, topCrews, topSessionsByDamage } = Route.useLoaderData();

	return (
		<Page>
			<div className="flex items-center justify-between">
				<Heading>Ranking</Heading>
				<CsvExportButton />
			</div>

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
				<div>
					<Heading mode="tertiary">Hoogst scorende sessies</Heading>
					{topSessions.length === 0 ? (
						<Small>Nog geen afgesloten sessies met scores.</Small>
					) : (
						<Table
							headers={['#', 'Locatie', 'Score', 'Datum & tijd']}
							colWidths={['5%', '35%', '20%', '40%']}
							rows={topSessions.map((s, i) => [
								<RankCell rank={i} />,
								s.locationName,
								<span className="font-semibold">{s.totalScore}</span>,
								formatDateTime(s.date),
							])}
						/>
					)}
					<br />
					<Heading mode="tertiary">Hoogste klanttevredenheid</Heading>
					{topSessionsByDamage.length === 0 ? (
						<Small>Nog geen afgesloten sessies gevonden.</Small>
					) : (
						<Table
							headers={['#', 'Locatie', 'Klanttevredenheid', 'Datum & tijd']}
							colWidths={['5%', '35%', '20%', '40%']}
							rows={topSessionsByDamage.map((s, i) => [
								<RankCell rank={i} />,
								s.locationName,
								<span className="font-semibold">{100 - s.systemDamage}</span>,
								formatDateTime(s.date),
							])}
						/>
					)}
				</div>

				<div>
					<Heading mode="tertiary">Hoogst scorende crews</Heading>
					{topCrews.length === 0 ? (
						<Small>Nog geen scores gevonden.</Small>
					) : (
						<Table
							headers={['#', 'Team', 'Score', 'Locatie', 'Datum & tijd']}
							rows={topCrews.map((c, i) => [
								<RankCell rank={i} />,
								<TeamBadge team={c.teamName} />,
								<span className="font-semibold">{c.bestScore}</span>,
								c.locationName,
								formatDateTime(c.date),
							])}
						/>
					)}
				</div>
			</div>
		</Page>
	);
}
